// Safe API utilities for handling JSON responses and preventing "Unexpected token <" errors

export interface ApiRequestOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public url: string,
    public responseText?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Safe JSON fetch wrapper that validates Content-Type before parsing
 * Prevents "Unexpected token <" errors when HTML is returned instead of JSON
 */
export async function safeJsonFetch<T = any>(options: ApiRequestOptions): Promise<T> {
  const { url, method = 'GET', headers = {}, body } = options;
  
  // Ensure we request JSON and provide authentication
  const requestHeaders: Record<string, string> = {
    'Accept': 'application/json',
    ...headers
  };

  // Add Content-Type for requests with body
  if (body) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined
    });

    // Check Content-Type before attempting JSON parsing
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!isJson) {
      // Got HTML or other non-JSON response - likely an error page or dev server fallback
      const responseText = await response.text();
      const preview = responseText.slice(0, 120);
      
      throw new ApiError(
        `Non-JSON response (${response.status}) from ${url}: Content-Type is "${contentType}". Response preview: ${preview}${responseText.length > 120 ? '...' : ''}`,
        response.status,
        url,
        responseText
      );
    }

    if (!response.ok) {
      // JSON error response
      try {
        const errorData = await response.json();
        throw new ApiError(
          errorData.error || errorData.message || `HTTP ${response.status}`,
          response.status,
          url
        );
      } catch (parseError) {
        // Couldn't parse error as JSON
        throw new ApiError(
          `HTTP ${response.status} - ${response.statusText}`,
          response.status,
          url
        );
      }
    }

    // Success - parse JSON response
    return await response.json();
  } catch (error) {
    // Network error or fetch failed
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network/connection error
    throw new ApiError(
      `Network error accessing ${url}: ${error instanceof Error ? error.message : String(error)}`,
      0,
      url
    );
  }
}

/**
 * Convenience function for GET requests with authentication
 */
export async function safeGet<T = any>(url: string, token?: string): Promise<T> {
  return safeJsonFetch<T>({
    url,
    method: 'GET',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
}

/**
 * Convenience function for POST requests with authentication
 */
export async function safePost<T = any>(url: string, data: any, token?: string): Promise<T> {
  return safeJsonFetch<T>({
    url,
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: data
  });
}

/**
 * Convenience function for PUT requests with authentication
 */
export async function safePut<T = any>(url: string, data: any, token?: string): Promise<T> {
  return safeJsonFetch<T>({
    url,
    method: 'PUT',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: data
  });
}

/**
 * Convenience function for PATCH requests with authentication
 */
export async function safePatch<T = any>(url: string, data: any, token?: string): Promise<T> {
  return safeJsonFetch<T>({
    url,
    method: 'PATCH',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: data
  });
}

/**
 * Convenience function for DELETE requests with authentication
 */
export async function safeDelete<T = any>(url: string, token?: string): Promise<T> {
  return safeJsonFetch<T>({
    url,
    method: 'DELETE',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
}

/**
 * Get the appropriate base URL for API calls
 */
export function getApiBaseUrl(): string {
  return process.env.REACT_APP_VIDEO_SERVICE_URL || 'http://localhost:3003';
}

/**
 * Build API URL with base URL
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
}
